import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { ArrowLeft, ExternalLink, File, Paperclip, Send, Trash2 } from 'lucide-react';
import {
  Avatar,
  Banner,
  Button,
  ButtonLink,
  EmptyState,
  Skeleton,
  SkeletonText,
  Textarea,
} from '@/components/ui';
import { MessageAttachments } from '@/components/messages/MessageAttachments';
import {
  useMarkMessageRead,
  usePatientMessages,
  useSendPatientMessage,
  type PatientMessage,
} from '@/hooks/usePatientMessages';
import { useAttachMessageDocument, validateMessageAttachmentFile } from '@/hooks/useMessageAttachments';
import { useUploadDocument } from '@/hooks/useDocuments';
import { supabase } from '@/lib/supabase';
import { sendPatientMessageCompose } from '@/lib/sendPatientMessageCompose';
import { TYPE, TYPE_TABULAR } from '@/lib/typography';
import { cn } from '@/utils/cn';
import {
  formatInboxAppointmentWhen,
  formatInboxBubbleWhen,
  inboxLocale,
} from '@/utils/messageInboxFormat';
import { resolveMessageSender } from '@/utils/messageSender';
import {
  patientDisplayName,
  patientMessagesPath,
  type PatientMessageConversation,
} from '@/utils/patientMessagesPath';

export function InboxConversationPanel({
  conversation,
  clinicId,
  canReply,
  showBack,
  onBack,
}: {
  conversation: PatientMessageConversation | null;
  clinicId?: string | null;
  canReply: boolean;
  showBack: boolean;
  onBack: () => void;
}) {
  const { t, i18n } = useTranslation();
  const locale = inboxLocale(i18n.language);
  const patientId = conversation?.patient_id ?? null;
  const patientName = conversation ? patientDisplayName(conversation) : '';
  const messages = usePatientMessages(patientId, clinicId);
  const markRead = useMarkMessageRead();
  const markedIds = useRef(new Set<string>());
  const nextAppointment = useNextUpcomingAppointment(patientId);

  useEffect(() => {
    if (!patientId || !messages.data) return;
    const unread = messages.data.filter(
      (msg) => msg.sender === 'patient' && !msg.read_at && !markedIds.current.has(msg.id),
    );
    if (unread.length === 0) return;
    for (const msg of unread) markedIds.current.add(msg.id);
    void Promise.all(
      unread.map((msg) => markRead.mutateAsync({ message_id: msg.id, patient_id: patientId })),
    ).catch((err) => {
      console.error('Error marking messages read:', err);
    });
  }, [markRead, messages.data, patientId]);

  if (!conversation) {
    return (
      <EmptyState
        icon={<Send className="h-7 w-7" />}
        title={t('messages.inbox.select_thread_title', 'Vælg en samtale')}
        description={t(
          'messages.inbox.select_thread_body',
          'Åbn en tråd i listen for at læse og svare.',
        )}
        className="h-full justify-center"
      />
    );
  }

  return (
    <section className="flex min-h-0 min-w-0 flex-1 flex-col" aria-labelledby="inbox-conversation-title">
      <header className="flex shrink-0 items-start gap-s3 border-b border-ds-line px-s4 py-s4 nav:px-s5">
        {showBack ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onBack}
            aria-label={t('messages.inbox.back', 'Tilbage til listen')}
          >
            <ArrowLeft />
          </Button>
        ) : null}
        <Avatar name={patientName} size={40} />
        <div className="min-w-0 flex-1">
          <h2 id="inbox-conversation-title" className={cn(TYPE.section, 'text-ds-text')}>
            {patientName}
          </h2>
          {nextAppointment.data ? (
            <p className={cn(TYPE.small, TYPE_TABULAR, 'text-ds-muted')}>
              {t('messages.inbox.next_appointment', 'Næste aftale')}{' '}
              {formatInboxAppointmentWhen(nextAppointment.data, locale)}
            </p>
          ) : null}
        </div>
        <ButtonLink to={patientMessagesPath(conversation.patient_id)} variant="tertiary" size="sm">
          <ExternalLink />
          {t('messages.inbox.open_patient', 'Åbn patient')}
        </ButtonLink>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-s4 py-s5 nav:px-s5">
        {messages.error ? (
          <Banner
            tone="error"
            title={t('messages.error_loading')}
            action={
              <Button type="button" variant="secondary" size="sm" onClick={() => void messages.refetch()}>
                {t('app.retry')}
              </Button>
            }
          />
        ) : messages.isLoading ? (
          <ConversationSkeleton />
        ) : !messages.data || messages.data.length === 0 ? (
          <EmptyState
            icon={<Send className="h-7 w-7" />}
            title={t('messages.no_messages')}
            description={t('messages.no_messages_hint')}
          />
        ) : (
          <ol className="flex flex-col gap-s4">
            {messages.data.map((msg) => (
              <ConversationBubble
                key={msg.id}
                message={msg}
                patientName={patientName}
                locale={locale}
              />
            ))}
          </ol>
        )}
      </div>

      {canReply ? (
        <ReplyComposer patientId={conversation.patient_id} patientName={patientName} />
      ) : null}
    </section>
  );
}

function ConversationBubble({
  message,
  patientName,
  locale,
}: {
  message: PatientMessage;
  patientName: string;
  locale: ReturnType<typeof inboxLocale>;
}) {
  const { t } = useTranslation();
  const fromPatient = message.sender === 'patient';
  const sender = resolveMessageSender(message, { patientName });
  const when = formatInboxBubbleWhen(message.created_at, locale);
  const outboundRead = !fromPatient && message.read_at ? ` · ${t('messages.read_at')}` : '';

  return (
    <li className={cn('flex flex-col', fromPatient ? 'items-start' : 'items-end')}>
      <div
        className={cn(
          TYPE.control,
          'max-w-[76%] whitespace-pre-wrap px-3.5 py-2.5 text-ds-text',
          fromPatient
            ? 'rounded-[14px] rounded-bl border border-ds-line bg-ds-sunk'
            : 'rounded-[14px] rounded-br bg-ds-teal-tint',
        )}
      >
        {message.content}
        <MessageAttachments messageId={message.id} />
      </div>
      <p
        className={cn(
          TYPE.meta,
          TYPE_TABULAR,
          'mt-s1 font-medium text-ds-muted',
          fromPatient ? '' : 'text-right',
        )}
      >
        {sender.name} · {when}
        {outboundRead}
      </p>
    </li>
  );
}

function ReplyComposer({
  patientId,
  patientName,
}: {
  patientId: string;
  patientName: string;
}) {
  const { t } = useTranslation();
  const sendMessage = useSendPatientMessage();
  const uploadDocument = useUploadDocument();
  const attachDocument = useAttachMessageDocument(undefined);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [body, setBody] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [sending, setSending] = useState(false);
  const firstName = patientName.trim().split(/\s+/)[0] || patientName;

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = Array.from(e.target.files || []);
    const valid: File[] = [];
    for (const file of next) {
      const validationError = validateMessageAttachmentFile(file);
      if (validationError === 'size_limit_exceeded') {
        toast.error(t('documents.errors.size_limit', `Filen ${file.name} er for stor (max 50 MB)`));
      } else if (validationError === 'invalid_type') {
        toast.error(t('documents.errors.invalid_type', `Filtypen for ${file.name} er ikke tilladt`));
      } else {
        valid.push(file);
      }
    }
    setFiles((prev) => [...prev, ...valid]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!body.trim() && files.length === 0) return;
    setSending(true);
    try {
      await sendPatientMessageCompose({
        patientId,
        content: body,
        files,
        sendMessage: (params) => sendMessage.mutateAsync(params),
        uploadDocument: (params) => uploadDocument.mutateAsync(params),
        attachDocument: (params) => attachDocument.mutateAsync(params),
      });
      toast.success(t('messages.toasts.sent', 'Besked sendt'));
      setBody('');
      setFiles([]);
    } catch (err) {
      console.error('Error sending message:', err);
      toast.error(t('messages.errors.send_failed', 'Kunne ikke sende besked'));
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={(e) => void onSubmit(e)} className="shrink-0 border-t border-ds-line px-s4 py-s4 nav:px-s5 nav:pb-s5">
      <Textarea
        id="inbox-reply"
        label={t('messages.inbox.reply_to', 'Svar til {{name}}', { name: firstName })}
        placeholder={t('messages.inbox.reply_placeholder', 'Skriv et svar…')}
        rows={3}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        disabled={sending}
        className="min-h-24"
      />
      <input
        ref={fileInputRef}
        type="file"
        onChange={handleFileSelect}
        multiple
        accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx,.xls,.xlsx,.txt"
        className="hidden"
      />
      {files.length > 0 ? (
        <ul className="mt-s3 space-y-s2">
          {files.map((file, index) => (
            <li
              key={`${file.name}-${index}`}
              className="flex items-center justify-between gap-s2 rounded-ctl bg-ds-sunk px-s3 py-s2"
            >
              <span className="flex min-w-0 items-center gap-s2">
                <File className="h-4 w-4 shrink-0 text-ds-muted" aria-hidden />
                <span className={cn(TYPE.small, 'truncate text-ds-text')}>{file.name}</span>
              </span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => setFiles((prev) => prev.filter((_, i) => i !== index))}
                disabled={sending}
                aria-label={t('messages.compose.remove_file', 'Fjern fil')}
              >
                <Trash2 />
              </Button>
            </li>
          ))}
        </ul>
      ) : null}
      <div className="mt-s3 flex items-center gap-s2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          disabled={sending}
        >
          <Paperclip />
          {t('messages.inbox.attach_file', 'Vedhæft fil')}
        </Button>
        <div className="ml-auto">
          <Button
            type="submit"
            disabled={sending || (!body.trim() && files.length === 0)}
            loading={sending}
            loadingText={t('messages.inbox.send_reply_sending', 'Sender svar…')}
          >
            <Send />
            {t('messages.inbox.send_reply', 'Send svar')}
          </Button>
        </div>
      </div>
    </form>
  );
}

function ConversationSkeleton() {
  return (
    <div className="flex flex-col gap-s4">
      <div className="ml-auto w-3/4">
        <Skeleton className="h-16 rounded-[14px]" />
        <SkeletonText lines={1} className="mt-s2 items-end" />
      </div>
      <div className="w-3/4">
        <Skeleton className="h-16 rounded-[14px]" />
        <SkeletonText lines={1} className="mt-s2" />
      </div>
    </div>
  );
}

function useNextUpcomingAppointment(patientId: string | null) {
  return useQuery({
    queryKey: ['messages', 'inbox', 'next-appointment', patientId] as const,
    enabled: Boolean(patientId),
    queryFn: async (): Promise<string | null> => {
      if (!patientId) return null;
      const { data, error } = await supabase
        .from('appointments')
        .select('starts_at')
        .eq('patient_id', patientId)
        .eq('status', 'upcoming')
        .gte('starts_at', new Date().toISOString())
        .order('starts_at', { ascending: true })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data?.starts_at ?? null;
    },
    staleTime: 30_000,
  });
}
